import Immutable from "immutable";
import camelcase from "lodash.camelcase";
import RecordClassCreator, { ManyResourcePayload, SingleResourcePayload } from "services/record";
import { AnalyticsReportFilterAttributes } from "components/Pages/AnalyticsPage/services";

type filterName = Exclude<keyof AnalyticsReportFilterAttributes, "startDate"|"endDate">;

interface AnalyticsReportAttributes {
  reportTitle: string;
  reportDescription: string;
  dataType: string;
  reportType: "list_items" | "time_series";
  reportDisplay: "line_chart" | "bar_chart" | "table";
  allowedFilters: Immutable.List<filterName>;
  isFavorite: boolean;
  isFeatured: boolean;
  lastViewed: string; // ISO date string e.g. 2020-03-30T20:43:11Z
  dataLabels: Immutable.List<string>; // list of column names
  imageUrl?: string; // URL for featured image, only used if isFeatured = true
  isPaginated?: boolean;
}

export default class AnalyticsReport extends RecordClassCreator<AnalyticsReportAttributes>({
  type: "analyticsReport",
  reportTitle: null,
  reportDescription: null,
  dataType: null,
  reportType: null,
  reportDisplay: null,
  allowedFilters: Immutable.List(),
  isFavorite: null,
  isFeatured: null,
  lastViewed: null,
  dataLabels: Immutable.List(),
  imageUrl: null,
  isPaginated: false
}) {
  static getFetchManyUrl(): string {
    return "/analytics_reports";
  }

  static getFetchOneUrl(id: string): string {
    return `/analytics_reports/${id}`;
  }

  static resolveFetchOneResult(payload: {
    data: {
      _id: string;
      _type: string;
      allowed_filters: string[];
      [key: string]: unknown; // keys of AnalyticsReportAttributes in snake case
    };
  }): SingleResourcePayload {
    const { data } = payload;

    return {
      ...payload,
      data: {
        ...data,
        dataType: data._id,
        allowedFilters: data.allowed_filters && data.allowed_filters.map(f => camelcase(f))
      }
    };
  }

  static resolveFetchManyResult(payload: {
    data: {
      _id: string;
      _type: string;
      allowed_filters: string[];
      [key: string]: unknown;
    }[];
  }): ManyResourcePayload {
    return {
      ...payload,
      data: payload.data.map(data => ({
        ...data,
        dataType: data._id,
        allowedFilters: data.allowed_filters.map(f => camelcase(f))
      }))
    };
  }
}
